import {
  Document, Packer, Paragraph, HeadingLevel, TextRun, Table, TableRow, TableCell,
  WidthType, AlignmentType, BorderStyle, PageNumber, Header, Footer,
} from 'docx';
import type { ModuleEntry, Section, Topic, TrainingDoc, Track } from '../content/types';
import type { TrainingViewer } from './access';
import { allows, visibleSections } from './access';

/**
 * Word export of the training guide.
 *
 * Built from the SAME content objects the page renders — never a second copy of
 * the words. That is the whole point of the single-source design: if this file
 * re-typed anything, the document and the app would start disagreeing the first
 * time someone edited one of them.
 *
 * ⚠️ ACCESS-FILTERED, exactly like the page. A PM's download contains no admin
 * instructions, and a field leader's contains no roll-ups. The alternative —
 * one canonical file for everyone — was considered and rejected: the guide's
 * value is that it only shows what you can reach, and a document that quietly
 * drops that property is worse than no document, because nobody can tell which
 * kind they are holding. The cover page therefore states whose copy it is.
 */

/** **bold** is the only inline formatting the content uses. */
function runs(text: string, opts: { size?: number; color?: string } = {}): TextRun[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p) => {
    const bold = p.startsWith('**') && p.endsWith('**');
    return new TextRun({ text: bold ? p.slice(2, -2) : p, bold, ...opts });
  });
}

const body = (text: string, opts: { indent?: number; spacing?: number; color?: string } = {}) =>
  new Paragraph({
    children: runs(text, { size: 21, color: opts.color }),
    spacing: { after: opts.spacing ?? 120 },
    indent: opts.indent ? { left: opts.indent } : undefined,
  });

function topicBlock(t: Topic): Paragraph[] | (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [
    new Paragraph({
      children: [
        new TextRun({ text: t.title, bold: true, size: 23 }),
        ...(t.owner ? [new TextRun({ text: `   Owned by ${t.owner}`, size: 17, color: '767676' })] : []),
      ],
      spacing: { before: 200, after: 80 },
      keepNext: true,
    }),
    body(t.summary),
  ];

  for (const [i, s] of (t.steps ?? []).entries()) {
    out.push(new Paragraph({ children: runs(`${i + 1}.  ${s}`, { size: 21 }), indent: { left: 340 }, spacing: { after: 60 } }));
  }

  if (t.watchOut?.length) {
    out.push(new Paragraph({
      children: [new TextRun({ text: 'Watch out', bold: true, size: 18, color: '6B5D1F' })],
      spacing: { before: 100, after: 40 }, indent: { left: 340 },
    }));
    for (const w of t.watchOut) {
      out.push(new Paragraph({ children: runs(`•  ${w}`, { size: 19, color: '6B5D1F' }), indent: { left: 460 }, spacing: { after: 40 } }));
    }
  }

  if (t.table) {
    if (t.table.caption) {
      out.push(new Paragraph({ children: [new TextRun({ text: t.table.caption, bold: true, size: 17, color: '767676' })], spacing: { before: 120, after: 60 } }));
    }
    out.push(new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          tableHeader: true,
          children: t.table.headers.map((h) => new TableCell({
            shading: { fill: 'F5F5F4' },
            children: [new Paragraph({ children: [new TextRun({ text: h, bold: true, size: 18 })] })],
          })),
        }),
        ...t.table.rows.map((r) => new TableRow({
          children: r.map((c) => new TableCell({ children: [new Paragraph({ children: runs(c, { size: 18 }) })] })),
        })),
      ],
    }));
    out.push(new Paragraph({ text: '', spacing: { after: 100 } }));
  }

  // Deep links replace screenshots on the page; in a printed document a path is
  // more use than a dead hyperlink, so both render as "where to find it".
  const where = t.deepLink ?? t.deepLinkNote;
  if (where) out.push(body(`Where: ${where}`, { indent: 340, color: '767676' }));

  return out as Paragraph[];
}

function sectionBlocks(sections: Section[]): (Paragraph | Table)[] {
  const out: (Paragraph | Table)[] = [];
  for (const s of sections) {
    out.push(new Paragraph({ text: s.title, heading: HeadingLevel.HEADING_2, spacing: { before: 320, after: 80 } }));
    if (s.intro) out.push(body(s.intro, { color: '5A5A56' }));
    for (const t of s.topics) out.push(...topicBlock(t));
  }
  return out;
}

/**
 * @param viewer the signed-in reader — the document is filtered to them
 * @param who    a human label for the cover page, e.g. "Project Manager"
 */
export function buildTrainingDocx(doc: TrainingDoc, viewer: TrainingViewer, who: string): Document {
  const children: (Paragraph | Table)[] = [];

  // Cover.
  children.push(
    new Paragraph({ text: doc.title, heading: HeadingLevel.TITLE, spacing: { after: 120 } }),
    new Paragraph({
      children: [new TextRun({ text: `Revision ${doc.revision}  ·  reviewed ${doc.revised}`, size: 21, color: '767676' })],
      spacing: { after: 60 },
    }),
    // Says whose copy this is, because it is filtered — see the header note.
    new Paragraph({
      children: [new TextRun({ text: `Prepared for: ${who}`, size: 21, color: '767676' })],
      spacing: { after: 60 },
    }),
    new Paragraph({
      children: [new TextRun({
        text: 'This copy shows only what this reader can reach in the Project Tracker. Someone with different access will have a different copy.',
        size: 19, italics: true, color: '767676',
      })],
      spacing: { after: 400 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'DDDDDD', space: 8 } },
    }),
  );

  const foundations = visibleSections(viewer, doc.foundations);
  if (foundations.length) {
    children.push(new Paragraph({ text: 'Everyone starts here', heading: HeadingLevel.HEADING_1, spacing: { before: 200, after: 120 } }));
    children.push(...sectionBlocks(foundations));
  }

  for (const track of doc.tracks.filter((t: Track) => allows(viewer, t.access))) {
    // The Field Leader track is scoped by TRACK, not by reader — an export of it
    // must read the way a field leader's does, same rule as the page.
    const trackViewer: TrainingViewer =
      track.id === 'field-leader' ? { ...viewer, forceFieldLeaderScope: true } : viewer;
    const secs = visibleSections(trackViewer, track.sections);
    if (!secs.length) continue;
    children.push(new Paragraph({ text: track.label, heading: HeadingLevel.HEADING_1, pageBreakBefore: true, spacing: { after: 60 } }));
    children.push(body(track.audience, { color: '5A5A56' }));
    children.push(...sectionBlocks(secs));
  }

  const modules = doc.modules.filter((m: ModuleEntry) => allows(viewer, m.access));
  if (modules.length) {
    children.push(new Paragraph({ text: doc.modulesTitle, heading: HeadingLevel.HEADING_1, pageBreakBefore: true, spacing: { after: 120 } }));
    for (const m of modules) {
      const topics = m.topics.filter((t) => allows(viewer, t.access));
      if (!topics.length) continue;
      children.push(new Paragraph({ text: m.name, heading: HeadingLevel.HEADING_2, spacing: { before: 320, after: 40 } }));
      children.push(body(m.location, { color: '767676' }));
      children.push(body(m.whatItsFor, { color: '5A5A56' }));
      for (const t of topics) children.push(...topicBlock(t));
    }
  }

  return new Document({
    creator: 'East & Westbrook — Project Tracker',
    title: doc.title,
    description: `Revision ${doc.revision}, reviewed ${doc.revised}`,
    styles: {
      default: {
        document: { run: { font: 'Arial', size: 21 } },
        title: { run: { font: 'Arial', size: 44, bold: true, color: '363636' } },
        heading1: { run: { font: 'Arial', size: 30, bold: true, color: '363636' } },
        heading2: { run: { font: 'Arial', size: 23, bold: true, color: '363636' }, paragraph: { spacing: { before: 300, after: 80 } } },
      },
    },
    sections: [{
      headers: {
        default: new Header({
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [new TextRun({ text: `${doc.title} — rev ${doc.revision}`, size: 16, color: '999999' })],
          })],
        }),
      },
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [new TextRun({ children: ['Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES], size: 16, color: '999999' })],
          })],
        }),
      },
      children,
    }],
  });
}

/** Build and hand the file to the browser. */
export async function downloadTrainingDocx(doc: TrainingDoc, viewer: TrainingViewer, who: string) {
  const blob = await Packer.toBlob(buildTrainingDocx(doc, viewer, who));
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Project Tracker — Training Guide (rev ${doc.revision}).docx`;
  a.click();
  // Revoked on the next tick rather than immediately: Safari aborts the download
  // if the object URL disappears in the same frame as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
